import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import { Button, EmptyState } from '../components/ui';

const DAYS = [
  { dow: 1, label: 'Pazartesi' },
  { dow: 2, label: 'Salı' },
  { dow: 3, label: 'Çarşamba' },
  { dow: 4, label: 'Perşembe' },
  { dow: 5, label: 'Cuma' },
  { dow: 6, label: 'Cumartesi' },
  { dow: 7, label: 'Pazar' },
];

export default function PlasiyerRoutesPage() {
  const [plasiyers, setPlasiyers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [stops, setStops] = useState([]);   // {tempId, day_of_week, account_id, sort_order, planned_time, visit_minutes, notes}
  const [accounts, setAccounts] = useState([]);
  const [accountSearch, setAccountSearch] = useState('');
  const [activeDay, setActiveDay] = useState(1);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [showAllAccounts, setShowAllAccounts] = useState(false);
  const isMobile = () => typeof window !== 'undefined' && window.innerWidth <= 768;
  const [customerListOpen, setCustomerListOpen] = useState(!isMobile());

  // Mobilde stops sayısı değişince (yeni durak eklendi) listeyi kapat
  useEffect(() => {
    if (isMobile() && stops.length > 0) {
      setCustomerListOpen(false);
      setAccountSearch('');
    }
  }, [stops.length]);

  // Cari arama alanı dışına tıklayınca listeyi kapat
  const customerSearchRef = useRef(null);
  useEffect(() => {
    if (!customerListOpen) return;
    const onDocClick = (e) => {
      if (customerSearchRef.current && !customerSearchRef.current.contains(e.target)) {
        setCustomerListOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
    };
  }, [customerListOpen]);

  const fetchPlasiyers = useCallback(async () => {
    try {
      const r = await api.get('/personnel/plasiyers');
      if (r.data.success) setPlasiyers(r.data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchAccounts = useCallback(async () => {
    try {
      const r = await api.get('/accounts', { params: { limit: 500 } });
      if (r.data.success) setAccounts((r.data.data.items || []).filter(a => a.is_active));
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchPlasiyers(); fetchAccounts(); }, [fetchPlasiyers, fetchAccounts]);

  const loadRoute = async (p) => {
    setSelected(p);
    setBusy(true);
    try {
      const r = await api.get('/plasiyer/route', { params: { plasiyer_id: p.id } });
      if (r.data.success) {
        setStops((r.data.data || []).map((s, i) => ({ ...s, tempId: 'srv-' + i })));
      }
    } catch (e) { console.error(e); }
    finally { setBusy(false); }
  };

  const dayStops = useMemo(() => {
    return stops.filter(s => s.day_of_week === activeDay).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [stops, activeDay]);

  const filteredAccounts = useMemo(() => {
    const q = accountSearch.toLowerCase();
    const selKey = selected ? String(selected.id).toLowerCase() : null;
    let list = accounts;
    // Plasiyer seçili ise: cari.sales_rep_id eşleşenleri filtrele (toggle ile tümü gösterilebilir)
    if (selKey && !showAllAccounts) {
      list = list.filter(a => a.sales_rep_id && String(a.sales_rep_id).toLowerCase() === selKey);
    }
    if (q) {
      list = list.filter(a =>
        (a.code || '').toLowerCase().includes(q) ||
        (a.title || '').toLowerCase().includes(q) ||
        (a.phone || '').includes(q) ||
        (a.mobile || '').includes(q)
      );
    }
    return list.slice(0, 100);
  }, [accounts, accountSearch, selected, showAllAccounts]);

  const assignedCount = useMemo(() => {
    if (!selected) return 0;
    const k = String(selected.id).toLowerCase();
    return accounts.filter(a => a.sales_rep_id && String(a.sales_rep_id).toLowerCase() === k).length;
  }, [accounts, selected]);

  const addStop = (account) => {
    const dayStops = stops.filter(s => s.day_of_week === activeDay);
    setStops(prev => [...prev, {
      tempId: 'new-' + Date.now() + '-' + Math.random().toString(36).slice(2,6),
      day_of_week: activeDay,
      account_id: account.id,
      account_code: account.code,
      account_title: account.title,
      address: account.address,
      city: account.city,
      sort_order: dayStops.length,
      planned_time: '',
      visit_minutes: 30,
      notes: '',
    }]);
    // useEffect (stops.length watcher) listeyi mobilde otomatik kapatır
  };

  const updateStop = (tempId, key, val) => {
    setStops(prev => prev.map(s => s.tempId === tempId ? { ...s, [key]: val } : s));
  };

  const removeStop = (tempId) => {
    setStops(prev => prev.filter(s => s.tempId !== tempId));
  };

  const moveStop = (tempId, dir) => {
    setStops(prev => {
      const dayList = prev.filter(s => s.day_of_week === activeDay).sort((a,b)=>(a.sort_order||0)-(b.sort_order||0));
      const idx = dayList.findIndex(s => s.tempId === tempId);
      if (idx < 0) return prev;
      const swapIdx = idx + dir;
      if (swapIdx < 0 || swapIdx >= dayList.length) return prev;
      const a = dayList[idx], b = dayList[swapIdx];
      return prev.map(s => {
        if (s.tempId === a.tempId) return { ...s, sort_order: b.sort_order };
        if (s.tempId === b.tempId) return { ...s, sort_order: a.sort_order };
        return s;
      });
    });
  };

  const save = async () => {
    if (!selected) return;
    setBusy(true); setMsg(null);
    try {
      const payload = {
        plasiyer_id: selected.id,
        stops: stops.map(s => ({
          day_of_week: s.day_of_week,
          account_id: s.account_id,
          sort_order: s.sort_order || 0,
          planned_time: s.planned_time || null,
          visit_minutes: s.visit_minutes || null,
          notes: s.notes || null,
        })),
      };
      const r = await api.put('/plasiyer/route', payload);
      if (r.data.success) {
        setMsg({ type: 'success', text: 'Rota kaydedildi' });
        setTimeout(() => setMsg(null), 3000);
      }
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || e.message });
    } finally { setBusy(false); }
  };

  return (
    <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 12, minHeight: 'calc(100vh - 80px)' }}>
      {/* Plasiyer üst seçici — yatay kaydırılabilir chip'ler */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 12, padding: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Plasiyer Seçin</span>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{plasiyers.length} kişi</span>
        </div>
        {plasiyers.length === 0 ? (
          <EmptyState
            compact
            title="Plasiyer yok"
            description='Personel kartında "Plasiyer (Saha)" işaretleyin.'
          />
        ) : (
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, WebkitOverflowScrolling: 'touch' }}>
            {plasiyers.map(p => {
              const active = selected?.id === p.id;
              return (
                <button key={p.id} type="button" onClick={() => loadRoute(p)}
                  style={{
                    flex: '0 0 auto', padding: '8px 14px', border: '1.5px solid ' + (active ? 'var(--accent-primary)' : 'var(--border-secondary)'),
                    background: active ? 'var(--accent-glow)' : 'var(--bg-input)',
                    color: active ? 'var(--accent-primary)' : 'var(--text-primary)',
                    borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
                    fontWeight: active ? 600 : 500, fontSize: '0.85rem', whiteSpace: 'nowrap',
                    transition: 'all 0.12s',
                  }}>
                  {p.full_name} <small style={{ opacity: 0.65, marginLeft: 4, fontSize: '0.72rem' }}>{p.code}</small>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Rota planlayıcı */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 12, padding: 14, flex: 1 }}>
        {!selected ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)' }}>
            Üstten bir plasiyer seçin
          </div>
        ) : (
          <>
            {msg && <div className={'detail-msg ' + msg.type} style={{ marginBottom: 10 }}>{msg.text}</div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid var(--border-secondary)' }}>
              <div>
                <h3 style={{ margin: 0 }}>{selected.full_name}</h3>
                <small style={{ color: 'var(--text-tertiary)' }}>{selected.code} — Haftalık Rota</small>
              </div>
              <Button size="sm" onClick={save} loading={busy}>Rotayı Kaydet</Button>
            </div>

            {/* Gün sekmeleri */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: '1px solid var(--border-secondary)', overflowX: 'auto' }}>
              {DAYS.map(d => {
                const cnt = stops.filter(s => s.day_of_week === d.dow).length;
                const active = activeDay === d.dow;
                return (
                  <button key={d.dow} type="button" onClick={() => setActiveDay(d.dow)}
                    style={{ padding: '8px 14px', border: 'none',
                      background: active ? 'var(--accent-primary)' : 'transparent',
                      color: active ? 'white' : 'var(--text-secondary)',
                      borderRadius: '8px 8px 0 0', cursor: 'pointer', fontFamily: 'inherit',
                      fontWeight: active ? 600 : 400, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                    {d.label} {cnt > 0 && <span style={{ background: active ? 'rgba(255,255,255,0.25)' : 'var(--accent-glow)', color: active ? 'white' : 'var(--accent-primary)', padding: '1px 7px', borderRadius: 10, marginLeft: 4, fontSize: '0.72rem' }}>{cnt}</span>}
                  </button>
                );
              })}
            </div>

            {/* İki sütun: müşteri arama + günün durakları (mobilde alt alta) */}
            <div className="plasiyer-route-grid">
              {/* Müşteri arama */}
              <div>
                <div className="musteri-ekle-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8, flexWrap: 'wrap', width: '100%', boxSizing: 'border-box' }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', flex: '1 1 auto', minWidth: 0 }}>
                    Müşteri Ekle {!showAllAccounts && <span style={{ color: 'var(--accent-primary)' }}>· Atanmış ({assignedCount})</span>}
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowAllAccounts(!showAllAccounts)}
                    style={{
                      fontSize: '0.72rem',
                      padding: '4px 10px',
                      borderRadius: 999,
                      border: '1px solid ' + (showAllAccounts ? 'var(--accent-primary)' : 'var(--border-secondary)'),
                      background: showAllAccounts ? 'rgba(59,130,246,0.1)' : 'transparent',
                      color: showAllAccounts ? 'var(--accent-primary)' : 'var(--text-secondary)',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      fontWeight: 500,
                      flexShrink: 0,
                    }}
                  >
                    {showAllAccounts ? '✓ Tümü' : 'Tüm cariler'}
                  </button>
                </div>
                <div ref={customerSearchRef}>
                <input value={accountSearch} onChange={e => { setAccountSearch(e.target.value); setCustomerListOpen(true); }} onFocus={() => setCustomerListOpen(true)} placeholder="Cari ara..." style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--border-input)', borderRadius: 6, background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '0.82rem', marginBottom: 6, boxSizing: 'border-box' }}/>
                {customerListOpen && (
                <div className="plasiyer-route-list">
                  {filteredAccounts.length === 0 && (
                    <div style={{ padding: 16, fontSize: '0.78rem', color: 'var(--text-tertiary)', textAlign: 'center' }}>
                      {!showAllAccounts && assignedCount === 0
                        ? 'Bu plasiyere atanmış cari yok. Cari kartında "Satış Temsilcisi" alanında plasiyeri seçin veya "Tümünü göster"i işaretleyin.'
                        : 'Sonuç yok'}
                    </div>
                  )}
                  {filteredAccounts.map(a => (
                    <div key={a.id} onClick={() => addStop(a)}
                      style={{ padding: '7px 10px', borderBottom: '1px solid var(--border-secondary)', cursor: 'pointer', fontSize: '0.8rem' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div style={{ fontWeight: 600 }}>{a.code} — {a.title}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{a.city || ''} {a.phone || a.mobile || ''}</div>
                    </div>
                  ))}
                </div>
                )}
                </div>
              </div>

              {/* Günün durakları */}
              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>{DAYS.find(d=>d.dow===activeDay)?.label} ({dayStops.length} durak)</label>
                <div className="plasiyer-route-list" style={{ padding: 4 }}>
                  {dayStops.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '0.82rem' }}>Bu gün için durak yok. Soldan müşteri seçin.</div>}
                  {dayStops.map((s, idx) => (
                    <div key={s.tempId} style={{ background: 'var(--bg-input)', border: '1px solid var(--border-secondary)', borderRadius: 6, padding: 8, marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
                        <strong style={{ color: 'var(--accent-primary)', minWidth: 22, flexShrink: 0 }}>{idx + 1}.</strong>
                        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          <div style={{ fontWeight: 600, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${s.account_code} — ${s.account_title}`}>{s.account_code} — {s.account_title}</div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{[s.address, s.city].filter(Boolean).join(', ')}</div>
                        </div>
                        <button onClick={() => moveStop(s.tempId, -1)} disabled={idx === 0} title="Yukarı" style={{ padding: 0, width: 26, height: 26, cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.3 : 1, border: '1px solid var(--border-secondary)', borderRadius: 4, background: 'transparent', color: 'var(--text-secondary)', flexShrink: 0, fontSize: 13 }}>↑</button>
                        <button onClick={() => moveStop(s.tempId, 1)} disabled={idx === dayStops.length-1} title="Aşağı" style={{ padding: 0, width: 26, height: 26, cursor: idx === dayStops.length-1 ? 'not-allowed' : 'pointer', opacity: idx === dayStops.length-1 ? 0.3 : 1, border: '1px solid var(--border-secondary)', borderRadius: 4, background: 'transparent', color: 'var(--text-secondary)', flexShrink: 0, fontSize: 13 }}>↓</button>
                        <button onClick={() => removeStop(s.tempId)} title="Sil" style={{ padding: 0, width: 26, height: 26, cursor: 'pointer', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 4, background: 'transparent', color: 'var(--danger)', flexShrink: 0, fontSize: 13 }}>✕</button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 90px) minmax(0, 70px) minmax(0, 1fr)', gap: 6, width: '100%' }}>
                        <input type="time" value={s.planned_time || ''} onChange={e => updateStop(s.tempId, 'planned_time', e.target.value)}
                          style={{ width: '100%', minWidth: 0, padding: '4px 6px', border: '1px solid var(--border-input)', borderRadius: 4, background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.78rem', boxSizing: 'border-box' }}/>
                        <input type="number" value={s.visit_minutes || ''} placeholder="dk" onChange={e => updateStop(s.tempId, 'visit_minutes', e.target.value)}
                          style={{ width: '100%', minWidth: 0, padding: '4px 6px', border: '1px solid var(--border-input)', borderRadius: 4, background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.78rem', boxSizing: 'border-box' }}/>
                        <input value={s.notes || ''} placeholder="Not" onChange={e => updateStop(s.tempId, 'notes', e.target.value)}
                          style={{ width: '100%', minWidth: 0, padding: '4px 6px', border: '1px solid var(--border-input)', borderRadius: 4, background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.78rem', boxSizing: 'border-box' }}/>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
